// VenuePage — root of /venue/[slug] SPA.
// Parses slug from window.location.pathname, fetches /api/venue-events?place_id
// and /api/venue-heat, renders header + event lists + parking voice + schema.org JSON-LD.

// Registry includes parking/transit voice duplicated from worker/parking-data.js.
// Per spec ship #2 bolder pass: small surface area (5 venues × 4 strings) so
// inline duplication beats a separate /api/parking round-trip for v1. Keep
// the values exact — character-for-character — so the curated voice stays
// canonical. Drift gets caught by the team when either file is touched.
// Per-venue SEO address fields — mirrors scrapers/venue-configs.js (street,
// postalCode, lat/lng) so the JSON-LD PostalAddress + GeoCoordinates emit
// the full set instead of just locality+region. Telephone + website pulled
// from public venue calendars; OMIT (don't emit empty strings) if unknown.
const VENUE_REGISTRY = [
  {
    slug: 'the-fillmore',
    canonical: 'The Fillmore',
    city: 'San Francisco',
    scale: 'mid',
    capacity: 1315,
    address: '1805 Geary Blvd, San Francisco, CA 94115',
    streetAddress: '1805 Geary Blvd',
    postalCode: '94115',
    lat: 37.783993,
    lng: -122.433136,
    telephone: '+1-415-346-6000',
    websiteUrl: 'https://www.thefillmore.com/',
    advice: 'Japan Center garages on Geary/Post are closest paid option. Street parking on Fillmore/Pine/Bush metered until 6pm then free but tight.',
    cost: '$15-25',
    tip: 'Good street parking on residential blocks west of Divisadero — meters end at 6pm.',
    transit: 'Muni 22-Fillmore / 38-Geary',
    venue_lore: {
      label: 'Fillmore tradition',
      body: 'Arrive 30 min early — the Fillmore prints a hand-drawn poster for every show and hands them out free at the door while supplies last.',
    },
  },
  {
    slug: 'fox-theater-oakland',
    canonical: 'Fox Theater Oakland',
    city: 'Oakland',
    scale: 'mid',
    capacity: 2800,
    address: '1807 Telegraph Ave, Oakland, CA 94612',
    streetAddress: '1807 Telegraph Ave',
    postalCode: '94612',
    lat: 37.808041,
    lng: -122.270233,
    telephone: '+1-510-302-2250',
    websiteUrl: 'https://thefoxoakland.com/',
    advice: 'Paid garages on Telegraph and Broadway are 1-2 blocks. Street parking on side streets is metered until 8pm in this zone.',
    cost: '$15-30',
    tip: 'BART 19th St Oakland is 3 blocks west — usually the move. Walk back to BART in groups after late shows.',
    transit: 'BART 19th St Oakland',
    venue_lore: null,
  },
  {
    // Oakland Arena — added 2026-05-27 as the 7th spine venue. Clusters with
    // Fox Theater Oakland in the rail; arena-scale Town counterpart. No
    // public calendar — Ticketmaster Discovery API is the source of truth via
    // scrapers/oakland-arena-bridge.js. Parking copy mirrors the Oakland Arena
    // entry in worker/parking-data.js so the page-level voice matches the
    // worker API. venue_lore is in OnSumn voice (Marco-authored).
    slug: 'oakland-arena',
    canonical: 'Oakland Arena',
    city: 'Oakland',
    scale: 'arena',
    capacity: 19596,
    address: '7000 Coliseum Way, Oakland, CA 94621',
    streetAddress: '7000 Coliseum Way',
    postalCode: '94621',
    lat: 37.750305,
    lng: -122.202835,
    telephone: '+1-510-569-2121',
    websiteUrl: 'https://www.theoaklandarena.com/',
    advice: 'Multiple paid stadium lots ring the arena complex. Pre-purchase via ParkMobile/SpotHero saves $5-15 vs day-of cash rates.',
    cost: '$25-50',
    tip: 'Lots fill 60-90 min before show. BART Coliseum Station is the move — pedestrian bridge drops you at the arena, ~10 min walk under cover.',
    transit: 'BART Coliseum Station (pedestrian bridge)',
    venue_lore: {
      label: 'The Town, arena scale',
      body: "The Town's biggest room. 19k deep when it has to be. Warriors used to ball here. Bay arena energy — the whole 510 fits.",
    },
  },
  {
    // Chase Center — Mission Bay arena (Warriors + Valkyries). TM-sourced via
    // scrapers/oakland-arena-bridge.js#bridgeChaseCenter. Parking strings
    // mirror worker/parking-data.js character-for-character.
    slug: 'chase-center',
    canonical: 'Chase Center',
    city: 'San Francisco',
    scale: 'arena',
    capacity: 18064,
    address: '1 Warriors Way, San Francisco, CA 94158',
    streetAddress: '1 Warriors Way',
    postalCode: '94158',
    lat: 37.76786,
    lng: -122.387466,
    websiteUrl: 'https://www.chasecenter.com/',
    advice: 'Pre-pay the official arena garage through Ticketmaster before you roll — walk-up event rates are brutal. Mission Bay/UCSF perimeter lots run cheaper if you\'re cool with a few blocks.',
    cost: '$30-120',
    tip: 'Skip the lot — your event ticket doubles as a free Muni pass, so ride the T-Third to the front door. Post-show the platform is a wall of people; grab a drink in Mission Bay for 20 and let the crush thin.',
    transit: 'Muni T-Third (UCSF/Chase Center stop) — event ticket = free ride; Metro from Embarcadero; ferry to Pier 48½ on event nights',
    venue_lore: {
      label: 'Mission Bay move',
      body: 'Event nights flip the neighborhood to special-event metering — street spots are a trap. Rideshare zones sit on Terry Francois Blvd and Warriors Way and surge the second the lights come up, so wait it out or walk to the Pier 48½ ferry, the water exit nobody fights over.',
    },
  },
  {
    // Levi's Stadium — Santa Clara (49ers + Bay FC), stadium-scale. Hosts only
    // the biggest tours (Ed Sheeran, AC/DC, Karol G, Usher+Chris Brown tier).
    // TM-sourced via scrapers/oakland-arena-bridge.js#bridgeLevisStadium.
    // Parking strings mirror worker/parking-data.js character-for-character.
    slug: 'levis-stadium',
    canonical: "Levi's Stadium",
    city: 'Santa Clara',
    scale: 'arena',
    capacity: 68500,
    address: '4900 Marie P DeBartolo Way, Santa Clara, CA 95054',
    streetAddress: '4900 Marie P DeBartolo Way',
    postalCode: '95054',
    lat: 37.40329,
    lng: -121.96949,
    websiteUrl: 'https://www.levisstadium.com/',
    advice: 'Buy the official color-coded lot pass on the Levi\'s site ahead of time — all lots are cashless, pre-pay only, no gate cash. Premium Red and Green sit closest; budget tiers park you a longer walk out.',
    cost: '$70-150',
    tip: 'VTA light rail to Great America drops you at the north gate — no lot, no exit crawl. If you drive, tailgate it out or leave during the encore, because Tasman and Great America Pkwy lock solid the second it ends.',
    transit: 'VTA Orange Line → Great America station; Caltrain to Mountain View → transfer to VTA Orange Line',
    venue_lore: {
      label: 'South Bay gridlock',
      body: 'Lots open 3.5-4 hours pre-show and the tailgate crowd fills Red and Green early — roll in late and you\'re circling. Rideshare pickup is geofenced off Tasman with 30-60 min waits, so walk toward the hotels to grab a driver faster and dodge the surge.',
    },
  },
  {
    slug: 'frost-amphitheater',
    canonical: 'Frost Amphitheater',
    city: 'Stanford',
    scale: 'arena',
    capacity: 6900,
    address: '351 Lasuen St, Stanford, CA 94305',
    streetAddress: '351 Lasuen St',
    postalCode: '94305',
    lat: 37.430752,
    lng: -122.166008,
    websiteUrl: 'https://live.stanford.edu/',
    advice: 'Stanford campus parking lots open 2 hours before show — Roth Way garage is closest. Many shows say "leave early, traffic on 280 is brutal."',
    cost: '$10-25 (campus lots) or free residential blocks west of campus',
    tip: 'Caltrain Palo Alto + Marguerite shuttle is an option for shows ending before 11pm. Otherwise drive in early.',
    transit: 'Caltrain Palo Alto + Marguerite shuttle',
    venue_lore: null,
  },
  {
    slug: 'sweetwater-music-hall',
    canonical: 'Sweetwater Music Hall',
    city: 'Mill Valley',
    scale: 'intimate',
    capacity: 300,
    address: '19 Corte Madera Ave, Mill Valley, CA 94941',
    streetAddress: '19 Corte Madera Ave',
    postalCode: '94941',
    lat: 37.907017,
    lng: -122.548103,
    telephone: '+1-415-388-3850',
    websiteUrl: 'https://sweetwatermusichall.com/',
    advice: 'Tiny downtown Mill Valley — street parking is the move. Lytton Square lots are the backup — be parked before doors on show nights.',
    cost: 'Free (street) or $5-10 (Lytton Square lot)',
    tip: 'No BART. Driving over the Golden Gate from SF is the typical path — leave SF 90 min before showtime on weekends.',
    transit: 'Golden Gate Transit 4/24/27 (limited evening service)',
    venue_lore: null,
  },
  {
    slug: 'the-catalyst',
    canonical: 'The Catalyst',
    city: 'Santa Cruz',
    scale: 'mid',
    capacity: 800,
    address: '1011 Pacific Ave, Santa Cruz, CA 95060',
    streetAddress: '1011 Pacific Ave',
    postalCode: '95060',
    lat: 36.971329,
    lng: -122.025688,
    telephone: '+1-831-713-5492',
    websiteUrl: 'https://catalystclub.com/',
    advice: 'Pacific Avenue downtown garages are 2 blocks. Street parking is metered until 6pm, then free.',
    cost: '$5-15',
    tip: 'Get downtown 45 min early on weekends for free street parking.',
    transit: 'Santa Cruz Metro Transit Center',
    venue_lore: null,
  },
  {
    slug: 'bill-graham-civic-auditorium',
    canonical: 'Bill Graham Civic Auditorium',
    city: 'San Francisco',
    scale: 'arena',
    capacity: 7000,
    address: '99 Grove St, San Francisco, CA 94102',
    streetAddress: '99 Grove St',
    postalCode: '94102',
    lat: 37.778164,
    lng: -122.417488,
    telephone: '+1-415-624-8900',
    websiteUrl: 'https://billgrahamcivic.com/',
    advice: 'Civic Center garage and Performing Arts garage both 2 blocks. Expect to walk 5-10 min from cheaper street parking on weekends.',
    cost: '$15-30',
    tip: 'BART Civic Center is 2 blocks away — almost always the move. Avoid Tenderloin street parking at night.',
    transit: 'BART Civic Center',
    venue_lore: null,
  },
  // ── ROOMS WE WATCH marquee-7 (2026-07-20) ──────────────────────────────
  // Mirrors data/venue-registry.js entries character-for-character on
  // slug/canonical/city/scale/capacity/lat/lng (per the drift note at the
  // top of that file). canonical = exact TM venue string — display polish
  // is a Rhea pass, not a rename (renames break dedup + heat join +
  // authority keys). advice/cost/tip/transit are transit-factual first
  // drafts — flagged for a Rhea voice pass in the marquee-7 PR.
  {
    slug: 'the-warfield',
    canonical: 'Warfield',
    city: 'San Francisco',
    scale: 'mid',
    capacity: 2300,
    address: '982 Market St, San Francisco, CA 94102',
    streetAddress: '982 Market St',
    postalCode: '94102',
    lat: 37.78260803222656,
    lng: -122.41034698486328,
    websiteUrl: 'https://www.thewarfieldtheatre.com/',
    advice: 'Fifth & Mission / Yerba Buena garage is the reliable play, 3 blocks down. Mid-Market street parking exists but you don\'t want it after dark.',
    cost: '$15-30',
    tip: 'Powell St BART/Muni is 2 blocks up Market — take it and skip the whole parking question. Stay on Market for the walk.',
    transit: 'BART/Muni Powell St (2 blocks)',
    venue_lore: null,
  },
  {
    slug: 'the-masonic',
    canonical: 'The Masonic',
    city: 'San Francisco',
    scale: 'mid',
    capacity: 3300,
    address: '1111 California St, San Francisco, CA 94108',
    streetAddress: '1111 California St',
    postalCode: '94108',
    lat: 37.791282653808594,
    lng: -122.41297149658203,
    websiteUrl: 'https://sfmasonic.com/',
    advice: 'The Masonic garage entrance is on the building\'s Taylor St side; Grace Cathedral garage sits across California St. Nob Hill street spots are scarce and permit-heavy.',
    cost: '$20-40',
    tip: 'The California cable car line stops out front and the 1-California runs the same ridge. From Powell BART it\'s a steep 15-min climb — budget for it or bus it.',
    transit: 'California cable car / Muni 1-California',
    venue_lore: null,
  },
  {
    slug: 'greek-theatre-berkeley',
    canonical: 'Greek Theatre-U.C. Berkeley',
    city: 'Berkeley',
    scale: 'arena',
    capacity: 8500,
    address: '2001 Gayley Rd, Berkeley, CA 94720',
    streetAddress: '2001 Gayley Rd',
    postalCode: '94720',
    lat: 37.87357711791992,
    lng: -122.25445556640625,
    websiteUrl: 'https://thegreekberkeley.com/',
    advice: 'UC lots off Gayley Rd and Stadium Rim Way open pre-show — roll in when they do. Southside street parking is a permit maze — read the signs.',
    cost: '$15-30 (campus lots)',
    tip: 'Downtown Berkeley BART + the 15-min uphill walk through campus is the classic route in. The walk back down post-show moves quick.',
    transit: 'BART Downtown Berkeley + 15-min campus walk',
    venue_lore: null,
  },
  {
    slug: 'the-regency-ballroom',
    canonical: 'The Regency Ballroom',
    city: 'San Francisco',
    scale: 'mid',
    capacity: 1400,
    address: '1300 Van Ness Ave, San Francisco, CA 94109',
    streetAddress: '1300 Van Ness Ave',
    postalCode: '94109',
    lat: 37.78785705566406,
    lng: -122.42150115966797,
    websiteUrl: 'https://www.theregencyballroom.com/',
    advice: 'AMC Van Ness garage is a block south; Cathedral Hill hotel lots take event traffic. Van Ness metered spots free up after 7 but go fast.',
    cost: '$15-25',
    tip: 'The 47/49 Van Ness BRT stops a block away and the 38-Geary crosses at O\'Farrell — bus beats parking here. Civic Center BART is a 12-min walk.',
    transit: 'Muni 47/49 Van Ness BRT / 38-Geary',
    venue_lore: null,
  },
  {
    slug: 'sap-center',
    canonical: 'SAP Center at San Jose',
    city: 'San Jose',
    scale: 'arena',
    capacity: 17562,
    address: '525 W Santa Clara St, San Jose, CA 95113',
    streetAddress: '525 W Santa Clara St',
    postalCode: '95113',
    lat: 37.33273696899414,
    lng: -121.90130615234375,
    websiteUrl: 'https://www.sapcenter.com/',
    advice: 'Official lots ring the arena — pre-purchase saves over gate rates. ABC lots on Autumn St run cheaper with a few blocks\' walk.',
    cost: '$20-40',
    tip: 'Diridon Station is directly across the street — Caltrain, ACE, Amtrak, and VTA light rail all land there. South Bay\'s easiest no-car arena run.',
    transit: 'Caltrain/ACE/Amtrak/VTA at Diridon (across the street)',
    venue_lore: null,
  },
  {
    slug: 'shoreline-amphitheatre',
    canonical: 'Shoreline Amphitheatre',
    city: 'Mountain View',
    scale: 'arena',
    capacity: 22500,
    address: '1 Amphitheatre Pkwy, Mountain View, CA 94043',
    streetAddress: '1 Amphitheatre Pkwy',
    postalCode: '94043',
    lat: 37.4267692565918,
    lng: -122.0806884765625,
    advice: 'Venue lots are the only real option — general parking is bundled with most tickets, premier lots sell separately and exit faster. Carpool or eat the crawl.',
    cost: 'Included-$60 (premier)',
    tip: 'Straight talk: no train gets you here — the nearest rail is ~2 miles out. Rideshare drop is on Shoreline Blvd; post-show pickup surges hard, so walk toward Crittenden Lane before you call it.',
    transit: 'No rail within 2 mi — rideshare/drive; Caltrain San Antonio + ~2 mi is the die-hard route',
    venue_lore: {
      label: 'Shoreline exit game',
      body: 'The lawn is the move on summer nights — bring a layer, the bayside fog rolls in by the encore. Amphitheatre Pkwy locks solid when it ends; premier parking or a long merch-line linger beats sitting in the lot.',
    },
  },
  {
    slug: 'the-independent',
    canonical: 'The Independent',
    city: 'San Francisco',
    scale: 'intimate',
    capacity: 500,
    address: '628 Divisadero St, San Francisco, CA 94117',
    streetAddress: '628 Divisadero St',
    postalCode: '94117',
    lat: 37.77552795410156,
    lng: -122.43760681152344,
    websiteUrl: 'https://www.theindependentsf.com/',
    advice: 'NoPa street parking tightens hard after 7 — try the blocks west of Divisadero. No dedicated garage; the DMV lot on Fell is a short walk when open.',
    cost: 'Free-$20 (street)',
    tip: 'The 24-Divisadero stops at the door and the 5-Fulton / 21-Hayes land a block off. 500-cap room — doors lines wrap the block on sold-out nights, roll early.',
    transit: 'Muni 24-Divisadero (at the door) / 5-Fulton / 21-Hayes',
    venue_lore: null,
  },
  {
    // Bottom of the Hill (Goal 2B, 2026-07-20) — Reza Tier-1 DIY room.
    // Copy is transit-factual first-draft; flagged for a Rhea voice pass.
    slug: 'bottom-of-the-hill',
    canonical: 'Bottom of the Hill',
    city: 'San Francisco',
    scale: 'intimate',
    capacity: 300,
    address: '1233 17th St, San Francisco, CA 94107',
    streetAddress: '1233 17th St',
    postalCode: '94107',
    lat: 37.76505661010742,
    lng: -122.39644622802734,
    websiteUrl: 'http://www.bottomofthehill.com/',
    advice: 'Street parking on the 17th St / Missouri / Texas blocks — the Potrero flats mostly free up after 6, tight on sold-out nights. No garage; don\'t block the driveways.',
    cost: 'Free-$10 (street)',
    tip: 'The 22-Fillmore runs right along 17th and drops you at the corner. Caltrain 22nd St is a ~15-min walk over the hill if you\'re coming up the Peninsula.',
    transit: 'Muni 22-Fillmore (17th St, at the corner) / Caltrain 22nd St ~15 min',
    venue_lore: {
      label: 'DIY church',
      body: 'All-ages since forever — the back patio between sets is half the show. Cash at the door for most nights; the calendar page IS the source of truth, hand-typed like it\'s 1998.',
    },
  },
  {
    // The Chapel (Goal 2A, 2026-07-20). Copy is transit-factual first-draft;
    // flagged for a Rhea voice pass.
    slug: 'the-chapel',
    canonical: 'The Chapel',
    city: 'San Francisco',
    scale: 'intimate',
    capacity: 500,
    address: '777 Valencia St, San Francisco, CA 94110',
    streetAddress: '777 Valencia St',
    postalCode: '94110',
    lat: 37.7604866027832,
    lng: -122.42135620117188,
    websiteUrl: 'https://thechapelsf.com/',
    advice: 'Valencia is a bike-corridor mess to park on — try the Bartlett garage at 21st or the side streets east of Mission. Weekend nights the whole corridor is hunted.',
    cost: '$10-25 (garage)',
    tip: 'BART 16th St Mission is 4 blocks; the 14-Mission and 33-Ashbury run closer. The room\'s a converted 1914 mortuary chapel — the arched ceiling is the best sound on Valencia.',
    transit: 'BART 16th St Mission (4 blocks) / Muni 14-Mission / 33-Ashbury',
    venue_lore: null,
  },
  {
    // Great American Music Hall (Goal 2A, 2026-07-20). Copy is
    // transit-factual first-draft; flagged for a Rhea voice pass.
    slug: 'great-american-music-hall',
    canonical: 'Great American Music Hall',
    city: 'San Francisco',
    scale: 'mid',
    capacity: 600,
    address: "859 O'Farrell St, San Francisco, CA 94109",
    streetAddress: "859 O'Farrell St",
    postalCode: '94109',
    lat: 37.7849235534668,
    lng: -122.41887664794922,
    websiteUrl: 'https://gamh.com/',
    advice: 'AMC Van Ness and the Holiday Inn garage on Van Ness are the close options — O\'Farrell street parking is Tenderloin-adjacent and not where you want to leave a car late.',
    cost: '$15-30',
    tip: 'The 38-Geary runs a block up, the 47/49 cross at Van Ness. 1907 room — balcony rail seats are the move if you\'re early, and the gold-leaf ceiling is worth arriving sober for.',
    transit: 'Muni 38-Geary (1 block) / 47/49 Van Ness',
    venue_lore: {
      label: 'The 1907 room',
      body: 'Built the year after the quake and it looks it — gilded balconies, oak floor, zero bad sightlines at 600 cap. Dinner tickets get the balcony tables; everyone else lines up on O\'Farrell.',
    },
  },
  {
    // The Midway (2026-08-27). Transit distances are measured against
    // data/stations.json (the GTFS-exact Bay Flows spine), not estimated:
    // Third St & Marin is 0.08 mi from the door and Caltrain 22nd St is
    // 0.62 mi.
    //
    // Voice pass 2026-08-27, off the venue's own FAQ (/connect/faq/). Two
    // corrections, not just wording: the first draft said the T platform was
    // "1 block" — the house says TWO, and 0.08 mi agrees with the house, so
    // the number moved. And the lore's "35-foot stage" is gone: the venue
    // publishes the Funktion-One Evo rig and the room's name (RiDE), but no
    // stage dimension anywhere, and an unsourced measurement is not shipped.
    // These four strings are duplicated in worker/parking-data.js — no gate
    // holds them in lockstep, so edit BOTH.
    slug: 'the-midway',
    canonical: 'The Midway',
    city: 'San Francisco',
    scale: 'mid',
    capacity: 1500,
    address: '900 Marin St, San Francisco, CA 94124',
    streetAddress: '900 Marin St',
    postalCode: '94124',
    lat: 37.749542236328125,
    lng: -122.38607025146484,
    websiteUrl: 'https://themidwaysf.com/',
    advice: 'Industrial Dogpatch, so the street grid is the lot — Marin, Indiana and Illinois run free most nights because nobody lives on those blocks. The house says it plainly for block parties: parking goes extremely limited, and they point people at rideshares. 280 lets out two streets over, which is also why the ramp knots the second a big room empties.',
    cost: 'Free (street)',
    tip: 'The T Third platform at Third & Marin is two blocks out — the venue\'s own count, and still closer than almost any room on this list. Caltrain 22nd St is a 12-minute walk north if you\'re coming up the Peninsula. There\'s a Bay Wheels dock next to the venue, but no racks for your own bike.',
    transit: 'Muni T Third (Third & Marin, 2 blocks) / Caltrain 22nd St ~12 min walk',
    venue_lore: {
      label: 'The Dogpatch complex',
      body: 'Forty thousand square feet of warehouse — main room, gallery and kitchen under one roof. The 1,500-cap hall is called RiDE and runs a Funktion-One Evo rig, which is why the electronic bookings route here instead of downtown.',
    },
  },
  // FEED-BACKED (2026-09-02) — data/venue-registry.js#FEED_VENUES. Schedule is
  // served from events.json by functions/venue/[[slug]].js; this room has no
  // scraper, no rail chip and no policy entry, and claims none.
  //
  // SOURCING, per Michael's thin-but-certain ruling: every line is published by
  // the venue or an encyclopedic source. `advice` and `cost` are ABSENT ON
  // PURPOSE — cobbscomedy.com publishes neither, and the garage names and rates
  // for them live only on parking aggregators, which go stale. A missing field
  // renders as absent; a stale garage sends a real person to the wrong block.
  {
    slug: 'cobb-s-comedy-club',
    canonical: "Cobb's Comedy Club",
    city: 'San Francisco',
    scale: 'intimate',
    capacity: 400,
    address: '915 Columbus Ave, San Francisco, CA 94133',
    streetAddress: '915 Columbus Ave',
    postalCode: '94133',
    lat: 37.802967,
    lng: -122.414215,
    telephone: '+1-415-928-4320',
    websiteUrl: 'https://www.cobbscomedy.com/',
    transit: 'Muni stops within a block — Columbus Ave & Lombard St',
    venue_lore: {
      label: 'How it got to Columbus Ave',
      body: 'A North Beach fixture since 1982, but not at this address — a fire drove it out of its original home in the Cannery, and it reopened here on Columbus in November 2002.',
    },
  },
  // THE 2026-09-02 CUT — fifteen feed-backed rooms, mirroring
  // data/venue-registry.js#FEED_VENUES character-for-character on slug,
  // canonical, city, address and coordinates (tests/feed-venues-cut.test.js
  // pins the alignment). WHY THEY MUST BE HERE: this component resolves
  // `VENUE_REGISTRY.find(slug)` on mount and sets `not_found` for a slug it
  // does not know — which would wipe the SSR'd schedule the Pages Function
  // just painted and show every visitor "Venue not on the map yet".
  //
  // Every editorial field is ABSENT on purpose — no capacity, scale, advice,
  // cost, tip, transit or lore. Venue-published sources only, and none has
  // been researched for these rooms yet; a blank renders as absent, an
  // approximation sends a real person to the wrong block.
  {
    slug: 'punch-line-comedy-club-san-francisco',
    canonical: 'Punch Line Comedy Club - San Francisco',
    city: 'San Francisco',
    address: '444 Battery St, San Francisco, CA 94111',
    streetAddress: '444 Battery St',
    postalCode: '94111',
    lat: 37.795506,
    lng: -122.400116,
  },
  {
    slug: 'neck-of-the-woods',
    canonical: 'Neck of the Woods',
    city: 'San Francisco',
    address: '406 Clement St, San Francisco, CA 94118',
    streetAddress: '406 Clement St',
    postalCode: '94118',
    lat: 37.783112,
    lng: -122.463753,
  },
  {
    slug: 'san-jose-center-for-the-performing-arts',
    canonical: 'San Jose Center for the Performing Arts',
    city: 'San Jose',
    address: '255 S Almaden Blvd, San Jose, CA 95113',
    streetAddress: '255 S Almaden Blvd',
    postalCode: '95113',
    lat: 37.32972,
    lng: -121.892548,
  },
  {
    slug: 'the-castro-theatre',
    canonical: 'The Castro Theatre',
    city: 'San Francisco',
    address: '429 Castro St, San Francisco, CA 94114',
    streetAddress: '429 Castro St',
    postalCode: '94114',
    lat: 37.762009,
    lng: -122.434723,
  },
  {
    slug: 'yoshi-s-oakland',
    canonical: "Yoshi's Oakland",
    city: 'Oakland',
    address: '510 Embarcadero W, Oakland, CA 94607',
    streetAddress: '510 Embarcadero W',
    postalCode: '94607',
    lat: 37.79583,
    lng: -122.278404,
  },
  {
    slug: 'cafe-du-nord',
    canonical: 'Cafe Du Nord',
    city: 'San Francisco',
    address: '2174 Market St, San Francisco, CA 94114',
    streetAddress: '2174 Market St',
    postalCode: '94114',
    lat: 37.766617,
    lng: -122.430443,
  },
  {
    slug: 'san-jose-improv',
    canonical: 'San Jose Improv',
    city: 'San Jose',
    address: '62 S 2nd St, San Jose, CA 95113',
    streetAddress: '62 S 2nd St',
    postalCode: '95113',
    lat: 37.335583,
    lng: -121.888512,
  },
  {
    slug: 'brick-and-mortar-music-hall',
    canonical: 'Brick and Mortar Music Hall',
    city: 'San Francisco',
    address: '1710 Mission St, San Francisco, CA 94103',
    streetAddress: '1710 Mission St',
    postalCode: '94103',
    lat: 37.769714,
    lng: -122.420441,
  },
  {
    slug: 'the-ritz',
    canonical: 'The Ritz',
    city: 'San Jose',
    address: '400 S 1st St, San Jose, CA 95113',
    streetAddress: '400 S 1st St',
    postalCode: '95113',
    lat: 37.329865,
    lng: -121.885635,
  },
  {
    slug: 'make-out-room',
    canonical: 'Make-Out Room',
    city: 'San Francisco',
    address: '3225 22nd St, San Francisco, CA 94110',
    streetAddress: '3225 22nd St',
    postalCode: '94110',
    lat: 37.755306,
    lng: -122.419365,
  },
  {
    slug: 'august-hall',
    canonical: 'August Hall',
    city: 'San Francisco',
    address: '420 Mason St, San Francisco, CA 94102',
    streetAddress: '420 Mason St',
    postalCode: '94102',
    lat: 37.787617,
    lng: -122.409843,
  },
  {
    slug: 'roxie-theater',
    canonical: 'Roxie Theater',
    city: 'San Francisco',
    address: '3117 16th St, San Francisco, CA 94103',
    streetAddress: '3117 16th St',
    postalCode: '94103',
    lat: 37.764626,
    lng: -122.42244,
  },
  {
    slug: 'the-uc-theatre',
    canonical: 'The UC Theatre',
    city: 'Berkeley',
    address: '2036 University Ave, Berkeley, CA 94704',
    streetAddress: '2036 University Ave',
    postalCode: '94704',
    lat: 37.871799,
    lng: -122.269829,
  },
  {
    slug: 'crybaby',
    canonical: 'Crybaby',
    city: 'Oakland',
    address: '1928 Telegraph Ave, Oakland, CA 94612',
    streetAddress: '1928 Telegraph Ave',
    postalCode: '94612',
    lat: 37.808605,
    lng: -122.269638,
  },
  {
    slug: 'rickshaw-stop',
    canonical: 'Rickshaw Stop',
    city: 'San Francisco',
    address: '155 Fell St, San Francisco, CA 94102',
    streetAddress: '155 Fell St',
    postalCode: '94102',
    lat: 37.776127,
    lng: -122.420517,
  },
];

function parseSlug() {
  const m = window.location.pathname.match(/^\/venue\/([a-z0-9-]+)/i);
  return m ? m[1].toLowerCase() : null;
}

// ────────── User-event merge helpers ──────────
// Surface user-submitted events from USER_EVENTS on the venue page so a
// venue with no scraper data (BGCA — no JSON-LD on their site) still has
// inventory. Mirrors worker/spine.js handleVenueUserEventsGet — both sides
// dedup against scraper VENUE_EVENTS rows by composite title+date key.

const _MONTH_IDX = { jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5, jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11 };

// "Jun 6" / "Jun 6 & 9" / "Jun 06" → "2026-06-06" (rolls forward a year if
// the date already passed by >60 days; matches worker/helpers.js
// computeExpiresAt rollover logic so client and server agree on the year).
function userDateToIsoDate(dateStr, refDate = new Date()) {
  if (!dateStr || typeof dateStr !== 'string') return null;
  // First-date canonicalization — handles ranges, multi-night, etc.
  const seps = /[&,\-–—\/]/;
  let s = dateStr.trim();
  const cut = s.search(seps);
  if (cut >= 0) s = s.slice(0, cut).trim();
  const m = s.match(/^([A-Za-z]{3})\s+0*(\d{1,2})$/);
  if (!m) return null;
  const monthIdx = _MONTH_IDX[m[1].toLowerCase()];
  const day = parseInt(m[2], 10);
  if (monthIdx === undefined || isNaN(day) || day < 1 || day > 31) return null;
  let year = refDate.getUTCFullYear();
  let candidate = new Date(Date.UTC(year, monthIdx, day));
  const sixtyDays = 60 * 24 * 60 * 60 * 1000;
  if (refDate.getTime() - candidate.getTime() > sixtyDays) {
    year += 1;
    candidate = new Date(Date.UTC(year, monthIdx, day));
  }
  return candidate.toISOString().slice(0, 10);
}

// Strip a tour-name suffix from a title so two versions that disagree only
// on the trailing " - Subtitle" still collide on the dedup key.
//   "Madison Beer - The Locket Tour"   -> "madison beer"
//   "Madison Beer – The Locket Tour"   -> "madison beer" (en-dash)
//   "Madison Beer — The Locket Tour"   -> "madison beer" (em-dash)
//   "Madison Beer"                     -> "madison beer"
// The dash must be surrounded by whitespace so "Self-Titled" or
// "Sigur Rós" don't get truncated mid-name.
function _stripTourSuffix(title) {
  return String(title || '').split(/\s+[-–—]\s+/)[0];
}

// Composite dedup key — normalized title (with tour suffix stripped) + first-date.
// Tour-suffix stripping fixes the "Madison Beer" (scraper) vs
// "Madison Beer - The Locket Tour" (user) double-card bug — both rows
// canonicalize to "madisonbeer|2026-06-23". Mirrors flyer.js's submission
// fingerprint rule minus the venue (already scoped by the page).
function _dedupKey(ev) {
  const stripped = _stripTourSuffix(ev.title);
  const t = stripped.toLowerCase().replace(/[^a-z0-9]/g, '');
  const d = (ev.iso_date || (ev.startDate ? ev.startDate.slice(0, 10) : '') || '').slice(0, 10);
  return `${t}|${d}`;
}

// D3 (2026-08-07): after the user/scraper merge below, the combined list
// passes the ONE shared net — window.ClientDedup.dedupeClientEvents gated
// by window.EventIdentity.duplicatePairVerdict, bucketed by
// window.EventIdentity.venueIsoBucketKey — so the venue list can never
// disagree with the feed, Spotlight, or /cards about what "same event"
// means. _dedupKey above stays as the user-vs-scraper MERGE key (it decides
// which fields survive a user submission landing on a scraper row); this
// net is the IDENTITY authority for two scraper rows describing one show,
// which that merge never compares. VENUE_EVENTS rows are venue-scoped and
// may not carry a `venue` field — the page's canonical fills in so the
// bucket key can anchor. Missing engine → pass-through: a visible duplicate
// over a silent deletion, the same doctrine as every other consumer.
function _netVenueList(rows, canonicalVenue) {
  const engine = typeof window !== 'undefined' ? window.ClientDedup : undefined;
  const identity = typeof window !== 'undefined' ? window.EventIdentity : undefined;
  if (!engine || !identity || typeof engine.dedupeClientEvents !== 'function'
      || typeof identity.duplicatePairVerdict !== 'function'
      || typeof identity.venueIsoBucketKey !== 'function') {
    return Array.isArray(rows) ? rows.slice() : [];
  }
  return engine.dedupeClientEvents(
    rows,
    identity.duplicatePairVerdict,
    (e) => identity.venueIsoBucketKey(
      (e && e.venue) ? e : { ...e, venue: canonicalVenue },
    ),
  );
}

// Compare two competing titles for the same dedup key. Returns true if
// `candidate` is "richer" than `current` — defined as having a tour-name
// suffix when the current doesn't, or a longer string when both have
// (or both lack) a suffix. Used by the merge below so the user-submitted
// "Madison Beer - The Locket Tour" wins over the scraper's bare
// "Madison Beer" without us having to rebuild the survivor by hand.
function _titleIsRicher(candidate, current) {
  const c = String(candidate || '');
  const x = String(current || '');
  const cHasSuffix = /\s+[-–—]\s+/.test(c);
  const xHasSuffix = /\s+[-–—]\s+/.test(x);
  if (cHasSuffix && !xHasSuffix) return true;
  if (!cHasSuffix && xHasSuffix) return false;
  // Both have (or both lack) a suffix — longer wins.
  return c.length > x.length;
}

// Project a USER_EVENTS record (already passed through worker projectEvent
// so wrapped fields are flat) into the shape VenueEventList + JSON-LD
// expects. Carries provenance: 'user' through as source_attribution so the
// existing INTEL chip path lights up for free.
// Rows the Pages Function left in <head> for a feed-backed room (see
// functions/venue/[[slug]].js#renderFeedIsland). Returns [] for spine rooms,
// which never get an island — their events come from VENUE_EVENTS as before.
// Deliberately tolerant: a malformed island must degrade to the "no shows"
// shell, never throw and blank the page.
function readFeedIsland() {
  try {
    const el = typeof document !== 'undefined' && document.getElementById('venue-feed-events');
    if (!el) return [];
    const parsed = JSON.parse(el.textContent || '[]');
    return Array.isArray(parsed) ? parsed.filter((e) => e && e.title && e.iso_date) : [];
  } catch {
    return [];
  }
}

function userEventToVenueShape(rec, place_id) {
  const iso = userDateToIsoDate(rec.date);
  const startDate = iso || rec.startDate || null;
  return {
    id: rec.slug || rec.id,
    event_hash: rec.slug || rec.id,
    place_id,
    title: rec.title,
    iso_date: iso,
    startDate,
    venue: rec.venue,
    category: rec.category,
    scale: rec.scale,
    capacity: rec.venueCapacity,
    eventStatus: rec.eventStatus || 'EventScheduled',
    expiresAt: rec.expiresAt,
    provenance: 'user',
    source_attribution: 'COMMUNITY',
  };
}

// Sweep any SSR-injected meta tags from the Pages Function (functions/venue/[[slug]].js)
// so React's per-count meta (richer copy with upcoming counts) doesn't pile on
// top as duplicates. Runs once per VenuePage mount before any React injection.
// Idempotent — second call is a no-op since the SSR tags are already gone.
//
// We DON'T remove the SSR <title> element — React updates the title via
// `document.title = ...` which mutates the existing element in place, so
// leaving it lets React swap text cleanly without re-creating the element.
let _ssrMetaSwept = false;
function sweepSsrMeta() {
  if (_ssrMetaSwept) return;
  _ssrMetaSwept = true;
  document.head.querySelectorAll('[data-ssr-meta], [data-ssr-jsonld], [data-ssr-canonical]').forEach(el => el.remove());
}

function injectJsonLd(jsonObj) {
  sweepSsrMeta();
  // Remove any previous JSON-LD this component added.
  const old = document.head.querySelector('script[data-venue-jsonld]');
  if (old) old.remove();
  const s = document.createElement('script');
  s.type = 'application/ld+json';
  s.dataset.venueJsonld = 'true';
  s.text = JSON.stringify(jsonObj);
  document.head.appendChild(s);
}

// Separate BreadcrumbList block — kept separate from the Place/Event block
// so each can be removed/refreshed independently and Search Console parses
// them as discrete graphs. SEO 1.
function injectBreadcrumbJsonLd(venue, slug) {
  const old = document.head.querySelector('script[data-venue-breadcrumb-jsonld]');
  if (old) old.remove();
  const obj = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://onsumn.com/' },
      // /venues pillar shipped 2026-05-27 (Vox Sprint 1) — this crumb now
      // resolves to a real page. Reciprocal link back lives at the bottom
      // of this VenuePage (VenuesPillarBacklink below).
      { '@type': 'ListItem', position: 2, name: 'Bay Venues', item: 'https://onsumn.com/venues' },
      { '@type': 'ListItem', position: 3, name: venue.canonical, item: `https://onsumn.com/venue/${slug}` },
    ],
  };
  const s = document.createElement('script');
  s.type = 'application/ld+json';
  s.dataset.venueBreadcrumbJsonld = 'true';
  s.text = JSON.stringify(obj);
  document.head.appendChild(s);
}

// SEO 4 — canonical link. Always use the slug from the route param so query
// strings (?ref=…, utm_*) or trailing-slash variants don't fragment indexing.
function injectCanonical(slug) {
  sweepSsrMeta();
  const old = document.head.querySelector('link[rel="canonical"][data-venue-canonical]');
  if (old) old.remove();
  const l = document.createElement('link');
  l.rel = 'canonical';
  l.dataset.venueCanonical = 'true';
  l.href = `https://onsumn.com/venue/${slug}`;
  document.head.appendChild(l);
}

// SEO 2 — build full PostalAddress from registry fields. Omits any field
// the venue doesn't have (don't emit empty strings — Google penalizes them).
function buildPostalAddress(venue) {
  const addr = {
    '@type': 'PostalAddress',
    addressRegion: 'CA',
    addressCountry: 'US',
  };
  // addressLocality is GUARDED like every other field, not assigned blind. Every
  // row in this registry carries a city today, so this changes no output — it
  // closes the shape that produced the Shoreline incident on the sibling
  // surface: worker/preview.js:2476 read `city || 'San Francisco'` into this same
  // property and published "SF" for a Mountain View amphitheatre. A locality is
  // a factual claim; it is emitted when the declared record states one, and
  // omitted otherwise. Never defaulted.
  if (venue.city) addr.addressLocality = venue.city;
  if (venue.streetAddress) addr.streetAddress = venue.streetAddress;
  if (venue.postalCode) addr.postalCode = venue.postalCode;
  return addr;
}

// SEO 3 — Rhea + Mav meta/OG copy templates. Three cases:
//   - N≥2 upcoming: title + count + next-up CTA
//   - 0 upcoming:   honest "no shows tracked yet — submit one" (BGCA-shape)
//   - 1 this week:  Mav FOMO lens (single-show urgency, no crying wolf)
// Voice: Bay-local, lowercase-first, specific > generic. City short ("SF" /
// "Oakland" / "Berkeley") with dedup when venueName already contains it
// (e.g., "Fox Oakland" — drop the trailing " Oakland").
function buildVenueMeta(venue, upcoming) {
  const venueName = venue.canonical;
  const fullLocality = venue.city || venue.addressLocality || '';
  const cityShort = /san francisco/i.test(fullLocality) ? 'SF' : fullLocality;
  const cityToken = cityShort && !venueName.toLowerCase().includes(cityShort.toLowerCase())
    ? ` ${cityShort}` : '';

  const formatShowDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return isNaN(d) ? '' : d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const sortedUpcoming = upcoming.slice().sort((a, b) =>
    Date.parse(a.iso_date || a.startDate) - Date.parse(b.iso_date || b.startDate)
  );
  const upcomingCount = sortedUpcoming.length;
  const next = sortedUpcoming[0];
  const nextShow = next?.title || '';
  const nextShowDate = formatShowDate(next?.iso_date || next?.startDate);
  const isThisWeek = next &&
    (Date.parse(next.iso_date || next.startDate) - Date.now() < 7 * 24 * 60 * 60 * 1000);

  if (upcomingCount === 0) {
    return {
      title: `${venueName}${cityToken} — shows, schedule, capacity`,
      metaDescription: `${venueName} in ${cityShort}. no upcoming shows tracked yet — know one that should be here? submit it on onsumn.`,
      ogTitle: `${venueName} in ${cityShort} — no shows tracked yet`,
      ogDescription: `${venueName} is on the spine but we haven't picked up any upcoming shows. spot one we missed? drop it in — onsumn tracks bay live music in real time.`,
    };
  }
  if (upcomingCount === 1 && isThisWeek) {
    return {
      title: `${nextShow} at ${venueName}${cityToken} ${nextShowDate}`,
      metaDescription: `${nextShow} playing ${venueName}${cityToken} ${nextShowDate}. one show on the books this week. lock it in on onsumn.`,
      ogTitle: `${nextShow} — ${venueName}${cityToken}, ${nextShowDate}`,
      ogDescription: `only show on ${venueName}'s calendar this week: ${nextShow}, ${nextShowDate}. small room, one night. see the full bay scene on onsumn.`,
    };
  }
  return {
    title: `${venueName}${cityToken} — ${upcomingCount} upcoming shows`,
    metaDescription: `what's playing at ${venueName} in ${cityShort}. ${upcomingCount} shows on deck, next up: ${nextShow} ${nextShowDate}. tracked live by onsumn.`,
    ogTitle: `${venueName} — ${upcomingCount} shows coming up in ${cityShort}`,
    ogDescription: `next up at ${venueName}: ${nextShow} ${nextShowDate}. plus ${upcomingCount - 1} more on the calendar. real shows, tracked live, no algorithm filler.`,
  };
}

// Apply buildVenueMeta's result to the DOM head. Sets <title> + meta description
// + og:title + og:description + twitter:card. Marked with data-venue-meta so
// re-renders replace in place (no duplicate tags piling up).
function injectVenueMeta(meta) {
  if (!meta) return;
  sweepSsrMeta();
  if (meta.title) document.title = meta.title;
  const ensure = (attrName, attrValue, content) => {
    if (!content) return;
    let el = document.head.querySelector(`meta[${attrName}="${attrValue}"][data-venue-meta]`);
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute(attrName, attrValue);
      el.dataset.venueMeta = 'true';
      document.head.appendChild(el);
    }
    el.setAttribute('content', content);
  };
  ensure('name',     'description',    meta.metaDescription);
  ensure('property', 'og:title',       meta.ogTitle);
  ensure('property', 'og:description', meta.ogDescription);
  ensure('name',     'twitter:card',   'summary_large_image');
}

// ───── Magazine-style section header (mirrored from VenueEventList) ─────
// Inlined here so the "Pulling Up" section reads the same as "Upcoming"
// without coupling the components. Same visual contract: leading 24x2 bar +
// uppercase Be Vietnam + trailing thin rule. currentColor → theme-aware.
function PullingUpHeader({ children }) {
  return (
    <h2 style={{
      fontFamily: "'Be Vietnam Pro', sans-serif",
      fontWeight: 800,
      // Matches SectionHeader in VenueEventList — fluid mobile→desktop.
      fontSize: 'clamp(18px, 4.6vw, 22px)',
      lineHeight: 1.1,
      textTransform: 'uppercase',
      letterSpacing: 2,
      display: 'flex',
      alignItems: 'center',
      gap: 16,
      margin: '0 0 24px',
    }}>
      <span style={{ width: 24, height: 2, background: 'currentColor', flexShrink: 0 }} />
      <span>{children}</span>
      <span style={{ flex: 1, height: 1, background: 'currentColor', opacity: 0.2 }} />
    </h2>
  );
}

// Small uppercase column heading — eucalyptus or fog accent comes from the
// left bar, so the H3 itself stays neutral for accessible contrast.
// Weight steps down from 800 (poster) to 700 (sub-head) for hierarchy.
function PullingUpColumnHeading({ children, className }) {
  return (
    <h3
      className={className}
      style={{
        fontFamily: "'Be Vietnam Pro', sans-serif",
        fontWeight: 700,
        fontSize: 12,
        lineHeight: 1.1,
        // 2px joins the unified labels-tier tracking scale (was 1.6).
        letterSpacing: 2,
        textTransform: 'uppercase',
        margin: '0 0 12px',
        color: 'var(--on-surface, inherit)',
      }}
    >{children}</h3>
  );
}

// "Pulling Up" — curated parking + transit voice. Renders ONLY for venues
// that have advice (the 5 in the spine). Other venues skip the section
// rather than render an empty shell.
function PullingUpSection({ venue, darkMode }) {
  // Mobile gutter alignment — match the 16px gutter used by the event list
  // and the inner header column. The minmax(280px, 1fr) auto-fit already
  // collapses to one column under 376px viewport, so no JS gating needed
  // for the column stack itself.
  // Read during render, not corrected in an effect — same first-paint reflow
  // the homepage had (lib/breakpoints.js#isBelow).
  const [isMobile, setIsMobile] = React.useState(
    () => (typeof window !== 'undefined' && window.bp ? window.bp.isMobile() : false),
  );
  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    // Normalized through the bp helper to the same xs2 (640px) boundary
    // as VenueEventList + VenueHeader. Was a one-off `(max-width: 639px)`
    // pre-fix — bp.maxWidth subtracts 0.02px so this doesn't overlap a
    // min-width listener at the same boundary.
    const mq = (window.bp && window.bp.maxWidth)
      ? window.bp.maxWidth('xs2')
      : window.matchMedia('(max-width: 639.98px)');
    const update = () => setIsMobile(mq.matches);
    update();
    if (mq.addEventListener) mq.addEventListener('change', update);
    else if (mq.addListener) mq.addListener(update);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', update);
      else if (mq.removeListener) mq.removeListener(update);
    };
  }, []);

  // Typical-minutes line (addendum §3). Lazily fetch the transit graph AFTER
  // the venue page is interactive (idle), compute the 3 nearest HUB_STATIONS by
  // dijkstra time from the venue's matched station, and render under the curated
  // Transit prose. Silence on every failure — no skeleton, no broken line
  // (below-the-fold, zero CLS when it stays empty). Only fires for spine venues
  // WITH coords (the section itself renders only when venue.advice exists); the
  // graph is read-only. Reuses the flows engine + §3 formatter via window
  // globals (venue.html loads FlowsTransitLib + format-reach-time). Point form
  // + HUB_LABELS per the 2026-07-15 ruling live inside venueHubMinutes.
  const [hubMinutes, setHubMinutes] = React.useState(null);
  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (!venue || !venue.advice) return undefined;
    if (!(typeof venue.lat === 'number' && isFinite(venue.lat))) return undefined;
    if (typeof window.venueHubMinutes !== 'function') return undefined;
    let cancelled = false;
    const compute = () => {
      // Absolute paths — venue pages live at /venue/<slug>, so a relative
      // "data/…" would resolve to /venue/data/… (404).
      Promise.all([
        fetch('/data/stations.json').then((r) => (r.ok ? r.json() : null)).catch(() => null),
        fetch('/data/station-graph.json').then((r) => (r.ok ? r.json() : null)).catch(() => null),
      ]).then(([stations, graph]) => {
        if (cancelled || !stations || !graph) return;
        try {
          const res = window.venueHubMinutes(venue, stations, graph);
          if (!cancelled && res && res.segments && res.segments.length) setHubMinutes(res);
        } catch (e) { /* silence — never a broken line */ }
      }).catch(() => { /* silence */ });
    };
    let handle;
    const ric = window.requestIdleCallback;
    if (typeof ric === 'function') handle = ric(compute, { timeout: 2000 });
    else handle = setTimeout(compute, 200);
    return () => {
      cancelled = true;
      if (typeof ric === 'function' && typeof window.cancelIdleCallback === 'function') window.cancelIdleCallback(handle);
      else clearTimeout(handle);
    };
  }, [venue]);

  if (!venue || !venue.advice) return null;
  return (
    <section style={{
      maxWidth: 1100,
      margin: '0 auto',
      padding: isMobile ? '8px 16px 48px' : '16px 24px 64px',
    }}>
      <PullingUpHeader>Pulling Up</PullingUpHeader>
      {venue.venue_lore && (
        // Side-stripe replacement (Zora 2026-05-26): Device D + A.
        // Tick marker on the kicker carries the zine pull-quote energy;
        // the 4px amber border-left is gone. Column structure preserved
        // via subtle top hairline so the block still reads as separated.
        <div style={{
          margin: '0 0 32px',
          paddingLeft: 0,
        }}>
          <div
            className="tick-marker tick-marker--amber"
            style={{
              fontFamily: "'Be Vietnam Pro', sans-serif",
              fontWeight: 700,
              fontSize: 10,
              letterSpacing: 2,
              textTransform: 'uppercase',
              color: 'var(--amber-gold-ink)',
              marginBottom: 6,
            }}
          >{venue.venue_lore.label}</div>
          <p style={{
            fontFamily: "'Barlow', sans-serif",
            fontStyle: 'italic',
            fontSize: 16,
            lineHeight: 1.55,
            margin: 0,
            color: 'var(--on-surface, inherit)',
          }}>{venue.venue_lore.body}</p>
        </div>
      )}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: 24,
      }}>
        {/* Parking column — Device A eucalyptus tick marker on the kicker.
            Was: 4px eucalyptus border-left + 16px paddingLeft (Zora ban). */}
        <div style={{ paddingLeft: 0 }}>
          <PullingUpColumnHeading className="tick-marker tick-marker--eucalyptus">Parking</PullingUpColumnHeading>
          <p style={{
            fontFamily: "'Barlow', sans-serif",
            fontSize: 16,
            // Body paragraphs: 1.6 for comfortable reading rhythm.
            lineHeight: 1.6,
            margin: '0 0 14px',
            color: 'var(--on-surface, inherit)',
          }}>{venue.advice}</p>
          <div style={{
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: 12,
            lineHeight: 1.4,
            // 1px snaps to body-uppercase tracking (was 0.5 orphan). The
            // figure ($15-25) is still mono with tabular-nums for clean rhythm.
            letterSpacing: 1,
            color: 'var(--amber-gold-ink)',
            marginBottom: 10,
            textTransform: 'uppercase',
            fontVariantNumeric: 'tabular-nums',
          }}>
            {/* Tiny "COST" label so the bare $-figure doesn't float context-less.
              * Mono + dim color + 2px tracking keeps it in the labels-tier scale.
              * 8px gap (via marginRight) gives generous breathing between label
              * and figure for clean column rhythm. */}
            <span style={{
              opacity: 0.9,
              letterSpacing: 2,
              marginRight: 8,
            }}>Cost</span>
            <span>{venue.cost}</span>
          </div>
          {venue.tip && (
            <p style={{
              fontFamily: "'Barlow', sans-serif",
              fontSize: 14,
              // Slightly looser line-height for the smaller, italic tip.
              lineHeight: 1.55,
              margin: 0,
              // AA-compliant fallback (was failing #888 at 3.5:1 on white).
              color: 'var(--on-surface-muted, #6B5B4F)',
              fontStyle: 'italic',
            }}>{venue.tip}</p>
          )}
        </div>

        {/* Transit column — Device A fog-blue tick marker.
            Was: 4px fog-blue border-left + 16px paddingLeft (Zora ban). */}
        <div style={{ paddingLeft: 0 }}>
          <PullingUpColumnHeading className="tick-marker tick-marker--fog">Transit</PullingUpColumnHeading>
          <p style={{
            fontFamily: "'Barlow', sans-serif",
            fontSize: 16,
            lineHeight: 1.6,
            margin: 0,
            color: 'var(--on-surface, inherit)',
          }}>{venue.transit}</p>
          {/* Typical-ride line (addendum §3) — a data appendix UNDER the curated
              prose. The registry voice above is never edited, truncated, or
              restyled. Absent below the 0.75mi match threshold or on any fetch/
              compute failure (venueHubMinutes returns null → this stays empty). */}
          {hubMinutes && hubMinutes.segments && hubMinutes.segments.length > 0 && (
            <div style={{
              marginTop: 12,
              display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', rowGap: 4,
            }}>
              {/* V1 label — mirrors the Parking column's COST device; doubles as
                  this surface's one honesty word (§3.2), so no extra footnote. */}
              <span aria-hidden="true" style={{
                fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                fontSize: 12, letterSpacing: 2, textTransform: 'uppercase',
                color: 'var(--on-surface-muted, #6B5B4F)', marginRight: 8,
              }}>Typical</span>
              {hubMinutes.segments.map((seg, i) => (
                <React.Fragment key={seg.hubId}>
                  {i > 0 && (
                    <span aria-hidden="true" style={{
                      color: 'var(--on-surface-muted, #6B5B4F)', margin: '0 8px',
                    }}>·</span>
                  )}
                  {/* V2 segment — fog ink (addendum §0.5 reach4 family; fog = the
                      Transit column's tick identity, deliberately not the reach
                      ramp). Exact hex per Iris's measured contrast: dark #A3BDCC
                      ≥9.8 / light #3F5E70 ≥6.2 on the warm-cream venue surface. */}
                  <span aria-hidden="true" style={{
                    fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                    fontSize: 12, letterSpacing: 1, textTransform: 'uppercase',
                    fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
                    color: darkMode ? '#A3BDCC' : '#3F5E70',
                  }}>{seg.text}</span>
                </React.Fragment>
              ))}
              {/* Screen-reader form — "about", never the "~" glyph (§6.3). */}
              <span style={{
                position: 'absolute', width: 1, height: 1, padding: 0, margin: -1,
                overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
              }}>
                {'Typical ride: ' + hubMinutes.segments.map((s) => s.aria).join(', ') + '.'}
              </span>
              {/* C1 — 511 §5(b) LICENSE-REQUIRED: the hub minutes above are
                  computed from the 511-derived transit graph (VenuePage
                  fetches stations.json + station-graph.json), so the tag line
                  rides with them — inside this conditional, so no data → no
                  credit. Mirrors FlowsConditions.jsx's register. Guard:
                  tests/attribution-sources.test.js. */}
              <span style={{ flexBasis: '100%', textAlign: 'right', lineHeight: 1, marginTop: 2 }}>
                <a
                  href="http://www.511.org"
                  target="_blank"
                  rel="noopener"
                  aria-label="Transit data provided by 511.org"
                  className="focus-double-ring"
                  style={{
                    display: 'inline-block',
                    fontFamily: 'var(--font-body)', fontSize: 9, fontWeight: 700,
                    letterSpacing: '0.1em', textTransform: 'uppercase',
                    color: 'var(--on-surface-muted, #6B5B4F)', textDecoration: 'none',
                  }}
                >DATA PROVIDED BY 511.ORG</a>
              </span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// Thin sticky nav strip — brand wordmark + escape hatch.
// Theme-aware via CSS vars; track-y uppercase per brutalist-editorial brief.
function VenueNav({ darkMode = true, setDarkMode }) {
  const [scrolled, setScrolled] = React.useState(false);
  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 200);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const toggleTheme = () => {
    const next = darkMode ? 'light' : 'dark';
    try { localStorage.setItem('onsumn-theme', next); } catch (e) {}
    document.documentElement.setAttribute('data-theme', next);
    if (setDarkMode) setDarkMode(next !== 'light');
  };

  return (
    <nav style={{
      position: 'sticky',
      // Honor the iOS notch — without this the wordmark hides under the cutout in landscape.
      top: 'env(safe-area-inset-top, 0px)',
      zIndex: 50,
      height: 44,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 20px',
      background: scrolled
        ? 'color-mix(in oklch, var(--surface, #121414) 92%, transparent)'
        : 'var(--surface, #121414)',
      backdropFilter: scrolled ? 'blur(10px)' : 'none',
      WebkitBackdropFilter: scrolled ? 'blur(10px)' : 'none',
      borderBottom: '1px solid color-mix(in oklch, var(--on-surface, #E2E2E2) 8%, transparent)',
    }}>
      <a href="/" style={{
        fontFamily: "'Be Vietnam Pro', sans-serif",
        fontWeight: 800,
        fontSize: 13,
        letterSpacing: 2,
        lineHeight: 1,
        color: 'var(--on-surface, #E2E2E2)',
        textDecoration: 'none',
      }}>ONSUMN</a>

      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <button
          onClick={toggleTheme}
          className="focus-double-ring"
          aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            // 44×44 hit area; visible icon kept at ~16px below.
            width: 44,
            height: 44,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--on-surface, #E2E2E2)',
            padding: 0,
          }}
        >
          {darkMode ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="4" />
              <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
            </svg>
          )}
        </button>
        <a href="/" className="focus-double-ring" style={{
          fontFamily: "'Be Vietnam Pro', sans-serif",
          fontWeight: 700,
          fontSize: 11,
          letterSpacing: 2,
          lineHeight: 1,
          textTransform: 'uppercase',
          color: darkMode ? 'var(--eucalyptus-green-ink)' : 'var(--eucalyptus-green, #395542)',
          textDecoration: 'none',
          // WCAG 2.5.5 — grow hit area to ~44×38 without changing visual size.
          // Nav is height:44 alignItems:center, so vertical padding stays centered.
          padding: '14px 4px',
        }}>← All events</a>
      </div>
    </nav>
  );
}

// Shared error-screen shell — brand fonts, retry slot, back-to-home anchor.
function VenueErrorScreen({ title, body, onRetry, retryLabel, darkMode, setDarkMode }) {
  return (
    // CLS GUARD (D19) — see the full explanation on the `!venue` loading branch
    // below. ALL THREE short branches must reserve, or none of them can: the
    // reservation parks <Footer/> at y=844, which is a zero-impact spot only
    // while whatever renders next is at least that tall. This screen is ~406px,
    // so without the reservation the footer walks UPWARD 438px into the viewport
    // — measured 0.2678 against a 0.1909 unreserved baseline, i.e. reserving
    // only the loading branch makes this path 40% WORSE than doing nothing.
    // Reached live whenever /api/venue-heat fails, which is not rare on slow
    // mobile connections.
    <div style={{ minHeight: '100vh' }}>
      <VenueNav darkMode={darkMode} setDarkMode={setDarkMode} />
      <div style={{
        padding: '64px 24px',
        maxWidth: 720,
        margin: '0 auto',
        textAlign: 'center',
      }}>
        <h1 style={{
          fontFamily: "'Be Vietnam Pro', sans-serif",
          fontWeight: 800,
          fontSize: 'clamp(28px, 5vw, 44px)',
          // Display kerning snaps to -1 (matches empty-state h1 and the NEXT
          // SHOW card title — was -0.5 orphan).
          letterSpacing: -1,
          lineHeight: 1.05,
          margin: 0,
          color: 'var(--on-surface, #E2E2E2)',
        }}>{title}</h1>
        {body && (
          <p style={{
            fontFamily: "'Barlow', sans-serif",
            fontSize: 17,
            // Body paragraphs target 1.55-1.65.
            lineHeight: 1.6,
            marginTop: 16,
            // CSS var resolves to AA-compliant per theme. #6B5B4F fallback
            // passes 6.5:1 on light; was using #A38D7C which fails (3.1:1) on light.
            color: 'var(--on-surface-muted, #6B5B4F)',
          }}>{body}</p>
        )}
        <div style={{
          marginTop: 32,
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
          alignItems: 'center',
        }}>
          {onRetry && (
            <button onClick={onRetry} style={{
              fontFamily: "'Be Vietnam Pro', sans-serif",
              fontWeight: 700,
              fontSize: 12,
              letterSpacing: 2,
              lineHeight: 1,
              textTransform: 'uppercase',
              padding: '12px 22px',
              // WCAG AA 44px tap target — inline-flex centering pins the label
              // regardless of font-size/line-height drift.
              minHeight: 44,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'var(--eucalyptus-green, #395542)',
              color: '#F8F6F2',
              border: 'none',
              borderRadius: 0,
              cursor: 'pointer',
              boxShadow: '4px 4px 0 var(--international-orange, #FF4F00)',
            }}>{retryLabel || 'Try again →'}</button>
          )}
          <a href="/" style={{
            fontFamily: "'Be Vietnam Pro', sans-serif",
            fontWeight: 700,
            fontSize: 11,
            letterSpacing: 2,
            lineHeight: 1,
            textTransform: 'uppercase',
            color: 'var(--amber-gold-ink)',
            textDecoration: 'none',
            marginTop: 4,
          }}>← Browse all events</a>
        </div>
      </div>
    </div>
  );
}

function VenuePage() {
  // Alias window-attached siblings inside the function body so JSX doesn't
  // try to render lowercase host elements (same pattern as D2 fix).
  const VenueHeader = window.VenueHeader;
  const VenueEventList = window.VenueEventList;

  // Mirror <html data-theme> into React state so children can prop-drill it.
  // Pre-React inline script in venue.html sets the attribute before first paint;
  // this hook keeps it in sync if the user toggles via VenueNav.
  const [darkMode, setDarkMode] = React.useState(() => {
    if (typeof document === 'undefined') return true;
    return document.documentElement.getAttribute('data-theme') !== 'light';
  });
  React.useEffect(() => {
    const html = document.documentElement;
    const observer = new MutationObserver(() => {
      setDarkMode(html.getAttribute('data-theme') !== 'light');
    });
    observer.observe(html, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  const slug = parseSlug();
  const reg = VENUE_REGISTRY.find(v => v.slug === slug);
  const [venue, setVenue] = React.useState(null);
  const [heat, setHeat] = React.useState(null);
  const [allEvents, setAllEvents] = React.useState([]);
  // Data age for the venue-gap dek (pique Phase 5) — the worker now stamps
  // /api/venue-events with `generated`; the registry's 24h ceiling READS
  // this and degrades on unknown, never defaults it (Zora spec §12).
  const [eventsGeneratedAt, setEventsGeneratedAt] = React.useState(null);
  const [parking, setParking] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [retryTick, setRetryTick] = React.useState(0);

  React.useEffect(() => {
    if (!reg) {
      setError('not_found');
      document.title = 'OnSumn — Venue not found';
      return;
    }
    document.title = `${reg.canonical} — Upcoming shows · OnSumn`;
    // SEO: per-venue meta description. Populated/updated again below once
    // we know the upcoming count. City parsed off the registry address
    // ("99 Grove St, San Francisco, CA 94102" → "San Francisco").
    (() => {
      // Sweep SSR meta tags first so React doesn't double up on description.
      sweepSsrMeta();
      const city = (reg.address || '').split(',')[1]?.trim() || 'the Bay';
      const desc = `Upcoming shows at ${reg.canonical}, ${city}. Lineup, dates, doors, parking, transit — the Bay's underground concert calendar.`;
      let meta = document.head.querySelector('meta[name="description"][data-venue-desc]');
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', 'description');
        meta.setAttribute('data-venue-desc', '');
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', desc.slice(0, 160));
    })();
    setError(null);
    let cancelled = false;

    // place_id comes straight off VENUE_REGISTRY (`reg`, parsed above), so the
    // events fetch is SCOPED server-side — ?place_id becomes a KV list prefix
    // instead of the whole-namespace scan that made this page hang 20–30s
    // (perf goal 2026-07-21). If a registry entry ever lacks place_id, fall
    // back to the unscoped URL so that venue still resolves. Heat still
    // fetches all blobs: the payload is one small row per venue and the page
    // picks its own out of the map by canonical below.
    // Also fetch user-submitted events for this venue so a venue with 0
    // scraper rows (BGCA — site has no JSON-LD) still surfaces community
    // submissions instead of rendering an empty "no shows" shell.
    Promise.all([
      fetch('/api/venue-heat').then(r => r.ok ? r.json() : { heat: {} }),
      fetch(`/api/venue-events${reg.place_id ? '?place_id=' + encodeURIComponent(reg.place_id) : ''}`)
        .then(r => r.ok ? r.json() : { events: [] }),
      fetch(`/api/venue-user-events?canonical=${encodeURIComponent(reg.canonical)}`)
        .then(r => r.ok ? r.json() : { events: [] })
        .catch(() => ({ events: [] })),
    ]).then(([heatJson, eventsJson, userEventsJson]) => {
      if (cancelled) return;
      setEventsGeneratedAt(typeof eventsJson.generated === 'string' ? eventsJson.generated : null);
      const heatMap = heatJson.heat || {};
      const ours = Object.values(heatMap).find(h => h.canonical === reg.canonical);
      const userEventRecords = userEventsJson.events || [];
      if (!ours) {
        // No heat blob from scraper — but user submissions may still exist
        // (BGCA path). Promote them onto the page using a null place_id;
        // VENUE_EVENTS filter below is fine with null since user events
        // ship through userEventToVenueShape (null place_id matches null).
        if (userEventRecords.length > 0) {
          const venueObj = { ...reg, place_id: null };
          setVenue(venueObj);
          const mapped = userEventRecords
            .map(r => userEventToVenueShape(r, null))
            .filter(e => e.iso_date || e.startDate);
          setAllEvents(_netVenueList(mapped, reg.canonical));
          return;
        }
        // Feed-backed room: no dedicated scraper, so no heat blob and nothing
        // in VENUE_EVENTS — but functions/venue/[[slug]].js already server-
        // rendered this venue's slice of events.json and left the same rows in
        // a <head> island. Without this the page would blank the shows the SSR
        // just painted and tell the visitor there are none.
        const feedRecords = readFeedIsland();
        if (feedRecords.length > 0) {
          setVenue({ ...reg, place_id: null });
          setAllEvents(_netVenueList(feedRecords, reg.canonical));
          return;
        }
        // Still render the venue shell (with scale chip from registry) so the user
        // sees something useful even before the scraper has touched this venue.
        setVenue({ ...reg, place_id: null });
        setError('no_data_yet');
        return;
      }
      const place_id = ours.place_id;
      const our = {
        ...reg, // scale + capacity + address from registry — survive empty heat blobs
        place_id,
        // heat-blob fields override registry only if present, so future scraper
        // changes (e.g. scale lands in heat) don't drift.
        scale: ours.scale || reg.scale,
        capacity: ours.capacity || reg.capacity,
      };
      setVenue(our);
      setHeat(ours);
      const scraperForUs = (eventsJson.events || []).filter(e => e.place_id === place_id);

      // Merge user events on top of scraper events. Dedup by composite
      // title+iso_date — scraper data wins on conflict for STRUCTURED fields
      // (offers.url, ageRestriction, capacity), but the survivor inherits
      // the RICHER title from either side so a user-submitted tour name
      // ("Madison Beer - The Locket Tour") isn't lost when the scraper
      // captured only the artist ("Madison Beer"). Same date, same venue,
      // just prefer the version with more info. Also surface the INTEL
      // chip when the user version was the source of the richer title.
      const userMapped = userEventRecords
        .map(r => userEventToVenueShape(r, place_id))
        .filter(e => e.iso_date || e.startDate);
      const merged = [...scraperForUs];
      const indexByKey = new Map();
      merged.forEach((ev, i) => indexByKey.set(_dedupKey(ev), i));
      for (const ue of userMapped) {
        const k = _dedupKey(ue);
        const existingIdx = indexByKey.get(k);
        if (existingIdx === undefined) {
          indexByKey.set(k, merged.length);
          merged.push(ue);
          continue;
        }
        const existing = merged[existingIdx];
        if (_titleIsRicher(ue.title, existing.title)) {
          merged[existingIdx] = {
            ...existing,
            title: ue.title,
            // Carry community signal forward so INTEL chip lights up when
            // the user supplied the richer title — even though scraper
            // remains the source of truth for structured fields.
            source_attribution: existing.source_attribution || ue.source_attribution,
          };
        }
      }
      // D3: the merge above resolves user-vs-scraper field survival; the
      // shared net resolves IDENTITY — two scraper rows for one show were
      // never compared by that merge at all.
      setAllEvents(_netVenueList(merged, reg.canonical));
    }).catch(() => { if (!cancelled) setError('fetch_failed'); });

    return () => { cancelled = true; };
  }, [reg?.slug, retryTick]);

  React.useEffect(() => {
    if (!venue || error) return;
    // Inject schema.org Place + nested Event array.
    const upcoming = allEvents.filter(e => {
      const t = Date.parse(e.iso_date || e.startDate || '');
      return !isNaN(t) && t >= Date.now();
    });
    const placeAddress = buildPostalAddress(venue);
    const placeType = venue.scale === 'arena' ? 'MusicVenue' : 'Place';
    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': placeType,
      name: venue.canonical,
      address: placeAddress,
      // SEO 2 — geo + telephone + url emit only if registry has them; OMIT
      // (no empty strings) for venues missing data so Google doesn't penalize.
      ...(typeof venue.lat === 'number' && typeof venue.lng === 'number'
        ? { geo: { '@type': 'GeoCoordinates', latitude: venue.lat, longitude: venue.lng } }
        : {}),
      ...(venue.telephone ? { telephone: venue.telephone } : {}),
      ...(venue.websiteUrl ? { url: venue.websiteUrl } : {}),
      ...(venue.venue_lore ? { description: venue.venue_lore.body } : {}),
      event: upcoming.map(e => ({
        '@type': 'Event',
        name: e.title,
        startDate: e.startDate || e.iso_date,
        eventStatus: `https://schema.org/${e.eventStatus || 'EventScheduled'}`,
        eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
        location: {
          '@type': placeType,
          name: venue.canonical,
          // Nested location reuses the full PostalAddress so each Event
          // child is independently valid for rich-result extraction.
          address: placeAddress,
        },
        offers: e.offers?.url ? { '@type': 'Offer', url: e.offers.url } : undefined,
      })),
    };
    injectJsonLd(jsonLd);
    // SEO 1 — BreadcrumbList alongside Place/Event block.
    injectBreadcrumbJsonLd(venue, slug);
    // SEO 4 — canonical link uses route-param slug.
    injectCanonical(slug);
    // SEO 3 — Rhea + Mav meta/OG copy injection (3 cases handled internally).
    injectVenueMeta(buildVenueMeta(venue, upcoming));
  }, [venue, allEvents, error, slug]);

  React.useEffect(() => {
    if (!venue || error) return;
    const upcomingCount = allEvents.filter(e => {
      const t = Date.parse(e.iso_date || e.startDate || '');
      return !isNaN(t) && t >= Date.now();
    }).length;

    // Remove any robots meta we previously set.
    const old = document.head.querySelector('meta[name="robots"][data-venue-robots]');
    if (old) old.remove();

    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.dataset.venueRobots = 'true';
    // Thin-content guard: noindex pages with < 3 upcoming events to avoid SERP penalty.
    // Always follow so Google walks links into our event pages.
    meta.content = upcomingCount < 3 ? 'noindex,follow' : 'index,follow';
    document.head.appendChild(meta);
  }, [venue, allEvents, error]);

  if (error === 'not_found') {
    return (
      <VenueErrorScreen
        title="Venue not on the map yet"
        body="We don't have this one tracked. Could be a typo in the URL — or it's a spot we haven't picked up yet."
        darkMode={darkMode}
        setDarkMode={setDarkMode}
      />
    );
  }
  if (error === 'fetch_failed') {
    return (
      <VenueErrorScreen
        title="Couldn't reach the calendar"
        body="The network blinked. Give it a beat and try again."
        onRetry={() => { setError(null); setRetryTick(t => t + 1); }}
        retryLabel="Try again →"
        darkMode={darkMode}
        setDarkMode={setDarkMode}
      />
    );
  }
  if (error === 'no_data_yet' && venue) {
    const scaleColors = {
      intimate: darkMode ? '#7A99AC' : '#4A6678',
      mid: '#D57800',
      arena: '#FF4F00',
      'mid-large': '#FF4F00',
    };
    // Fallback color for unknown scale — the warm-neutral muted pair, split
    // per theme like `intimate` (#6B5B4F alone is 2.73:1 on the dark chip).
    const scaleColor = scaleColors[venue.scale] || (darkMode ? '#A38D7C' : '#6B5B4F');
    return (
      // CLS GUARD (D19) — the `no_data_yet` branch. Same reservation and same
      // reason as VenueErrorScreen above: ~406px of content would let the
      // footer walk upward into the viewport (measured 0.2694 vs a 0.1898
      // unreserved baseline). Latent today — all 19 registry venues carry heat
      // blobs — but this branch is designed to fire for any newly-registered
      // venue, so it must reserve before that happens, not after.
      <div style={{ minHeight: '100vh' }}>
        <VenueNav darkMode={darkMode} setDarkMode={setDarkMode} />
        <div style={{
          padding: '48px 24px 64px',
          maxWidth: 720,
          margin: '0 auto',
        }}>
          <h1 style={{
            fontFamily: "'Be Vietnam Pro', sans-serif",
            fontWeight: 800,
            // Min 36px keeps poster scale on mobile.
            fontSize: 'clamp(36px, 6vw, 56px)',
            letterSpacing: -1,
            lineHeight: 1.0,
            margin: 0,
            color: 'var(--on-surface, #E2E2E2)',
          }}>{venue.canonical}</h1>
          <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{
              fontFamily: "'Be Vietnam Pro', sans-serif",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 2,
              lineHeight: 1,
              textTransform: 'uppercase',
              padding: '4px 10px',
              // 2px snaps tighter to the brand's sharp-corner mandate (was 3,
              // top of the allowed range; 2 reads cleaner against the 0-radius
              // poster chip).
              borderRadius: 2,
              // Amber/orange TEXT reads the -ink tokens (light 2.7:1 → 5.3:1);
              // the brand hex stays on the border and tint.
              color: { mid: 'var(--amber-gold-ink)', arena: 'var(--international-orange-ink)', 'mid-large': 'var(--international-orange-ink)' }[venue.scale] || scaleColor,
              border: `1px solid ${scaleColor}55`,
              background: `${scaleColor}10`,
              fontVariantNumeric: 'tabular-nums',
              // A feed-backed room carries neither a scale nor a venue-published
              // capacity — an empty chip is worse than none, and "undefined" is
              // worse than either. Hide it rather than print it.
              display: venue.scale || venue.capacity ? undefined : 'none',
            }}>{venue.scale || ''}{venue.capacity ? ` · ${venue.capacity.toLocaleString()} CAP` : ''}</span>
            <span style={{
              fontFamily: "'Barlow', sans-serif",
              fontSize: 14,
              lineHeight: 1.4,
              // AA-compliant fallback (#A38D7C fails 3.1:1 on light).
              color: 'var(--on-surface-muted, #6B5B4F)',
            }}>{venue.city}</span>
          </div>
          <p style={{
            fontFamily: "'Barlow', sans-serif",
            fontSize: 18,
            lineHeight: 1.6,
            marginTop: 28,
            color: 'var(--on-surface, #E2E2E2)',
          }}>No shows on the calendar right now. Check back — venues book ahead.</p>
          <a href="/" style={{
            display: 'inline-block',
            marginTop: 24,
            fontFamily: "'Be Vietnam Pro', sans-serif",
            fontWeight: 700,
            fontSize: 12,
            letterSpacing: 2,
            lineHeight: 1,
            textTransform: 'uppercase',
            color: 'var(--amber-gold-ink)',
            textDecoration: 'none',
          }}>← Browse all events</a>
        </div>
      </div>
    );
  }
  if (!venue) {
    return (
      // CLS GUARD (D19, 2026-07-29) — reserve a viewport of height while loading.
      // src/entry/venue-main.jsx:16 renders <Footer/> as a Fragment SIBLING of this
      // tree, so with no reservation the footer first paints at y=197 (VenueNav 44px
      // + the 64px-padded Loading div) and occupies 447px of an 844px mobile
      // viewport. When the /api/* Promise.all resolves, the real page expands
      // beneath it and the footer is displaced — impact 447/844 with the distance
      // fraction saturated at 1.0. Measured on production: CLS 0.5745, ~5.7x the
      // 0.1 "good" threshold, worst on the site.
      //
      // The score is footerHeightInViewport / viewportHeight and NOTHING else — the
      // displacement distance is irrelevant once it exceeds one viewport. So the only
      // lever is keeping the footer below the fold until data lands. Deleting the SSR
      // pre-render in functions/venue/[[slug]].js was measured at 0.5733 (-0.2%): it
      // is not the cause, do not "fix" this there.
      //
      // Do not remove this minHeight. tests/venue-loading-reserve.test.js fails on it.
      <div style={{ minHeight: '100vh' }}>
        <VenueNav darkMode={darkMode} setDarkMode={setDarkMode} />
        <div style={{
          padding: 64,
          textAlign: 'center',
          fontFamily: "'Barlow', sans-serif",
          // AA-compliant fallback in both themes (was #A38D7C which fails on light).
          color: 'var(--on-surface-muted, #6B5B4F)',
          fontSize: 16,
          lineHeight: 1.5,
        }}>Loading…</div>
      </div>
    );
  }

  // Sort & split upcoming vs. past (last 90 days).
  const now = Date.now();
  const ninetyDays = 90 * 24 * 60 * 60 * 1000;
  const upcoming = [...allEvents]
    .filter(e => {
      const t = Date.parse(e.iso_date || e.startDate || '');
      return !isNaN(t) && t >= now;
    })
    .sort((a, b) => Date.parse(a.iso_date || a.startDate) - Date.parse(b.iso_date || b.startDate));
  const past = [...allEvents]
    .filter(e => {
      const t = Date.parse(e.iso_date || e.startDate || '');
      return !isNaN(t) && t < now && now - t <= ninetyDays;
    })
    .sort((a, b) => Date.parse(b.iso_date || b.startDate) - Date.parse(a.iso_date || a.startDate));

  return (
    <div>
      <VenueNav darkMode={darkMode} setDarkMode={setDarkMode} />
      <VenueHeader venue={venue} heat={heat} nextEvent={upcoming[0]} darkMode={darkMode} />
      <VenueEventList upcoming={upcoming} past={past} darkMode={darkMode} eventsGeneratedAt={eventsGeneratedAt} />
      <PullingUpSection venue={venue} darkMode={darkMode} />
      <VenuesPillarBacklink />
    </div>
  );
}

// Hub-and-spoke reciprocal-link gate. Vox brief 2026-05-27 §"Internal Link
// Strategy / /venue/[slug] linking discipline" requires every spoke page
// to link back to /venues so PageRank doesn't leak out of the cluster.
// Lives after PullingUpSection so it reads as a closing-off action, not a
// competing nav. Plain anchor styling, eucalyptus-on-dark to match the
// VenueNav's "← All events" affordance.
function VenuesPillarBacklink() {
  return (
    <section
      aria-label="Other rooms"
      style={{
        maxWidth: 1100,
        margin: '0 auto',
        padding: '0 24px 64px',
        textAlign: 'left',
      }}
    >
      <div style={{
        paddingTop: 32,
        borderTop: '1px solid var(--outline-variant, #554335)',
      }}>
        <a
          href="/venues"
          className="focus-double-ring"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            minHeight: 44,
            padding: '12px 0',
            fontFamily: "'Be Vietnam Pro', sans-serif",
            fontWeight: 700,
            fontSize: 12,
            letterSpacing: 2,
            lineHeight: 1,
            textTransform: 'uppercase',
            color: 'var(--eucalyptus-green-ink)',
            textDecoration: 'none',
          }}
        >
          <span>Other rooms we watch</span>
          <span aria-hidden="true">&rarr;</span>
        </a>
      </div>
    </section>
  );
}

window.VenuePage = VenuePage;
